import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import SliderInput from '../../components/SliderInput';
import { Button } from '../../components/ui/button';

const pad = (n) => String(Math.floor(n)).padStart(2, '0');

const TimeCalculator = () => {
  const [h1, setH1] = useState(2);
  const [m1, setM1] = useState(30);
  const [s1, setS1] = useState(0);
  const [h2, setH2] = useState(1);
  const [m2, setM2] = useState(45);
  const [s2, setS2] = useState(30);
  const [op, setOp] = useState('add');

  const result = useMemo(() => {
    const t1 = h1 * 3600 + m1 * 60 + s1;
    const t2 = h2 * 3600 + m2 * 60 + s2;
    const total = op === 'add' ? t1 + t2 : t1 - t2;
    const abs = Math.abs(total);
    return {
      sign: total < 0 ? '−' : '',
      hours: Math.floor(abs / 3600),
      minutes: Math.floor((abs % 3600) / 60),
      seconds: Math.floor(abs % 60),
      totalSec: total,
    };
  }, [h1, m1, s1, h2, m2, s2, op]);

  return (
    <CalculatorLayout
      title="Time Calculator"
      subtitle="Add or subtract time durations in hours, minutes, and seconds."
      category="Other"
      description={<p>Add or subtract two time durations. Useful for calculating work hours, workout splits, video editing timing, and more.</p>}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="text-sm font-semibold text-slate-700 mb-3">Time 1</div>
            <div className="grid grid-cols-3 gap-3">
              <SliderInput label="Hours" value={h1} onChange={setH1} min={0} max={99} step={1} />
              <SliderInput label="Min" value={m1} onChange={setM1} min={0} max={59} step={1} />
              <SliderInput label="Sec" value={s1} onChange={setS1} min={0} max={59} step={1} />
            </div>
          </div>

          <div className="flex justify-center gap-2">
            <Button onClick={() => setOp('add')} variant={op === 'add' ? 'default' : 'outline'} className={op === 'add' ? 'bg-indgreen-500 hover:bg-indgreen-600 text-white' : ''}>+ Add</Button>
            <Button onClick={() => setOp('sub')} variant={op === 'sub' ? 'default' : 'outline'} className={op === 'sub' ? 'bg-saffron-500 hover:bg-saffron-600 text-white' : ''}>− Subtract</Button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="text-sm font-semibold text-slate-700 mb-3">Time 2</div>
            <div className="grid grid-cols-3 gap-3">
              <SliderInput label="Hours" value={h2} onChange={setH2} min={0} max={99} step={1} />
              <SliderInput label="Min" value={m2} onChange={setM2} min={0} max={59} step={1} />
              <SliderInput label="Sec" value={s2} onChange={setS2} min={0} max={59} step={1} />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Result</div>
          <div className="font-display text-4xl md:text-5xl font-bold text-bharat-navy display-font mt-1">
            {result.sign}{pad(result.hours)}:{pad(result.minutes)}:{pad(result.seconds)}
          </div>
          <div className="text-xs text-slate-500 mt-1">hh:mm:ss</div>
          <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
            <Box label="Total Hours" value={(Math.abs(result.totalSec) / 3600).toFixed(4)} />
            <Box label="Total Minutes" value={(Math.abs(result.totalSec) / 60).toFixed(2)} />
            <Box label="Total Seconds" value={Math.abs(result.totalSec).toLocaleString()} tone="saffron" />
            <Box label="Days" value={(Math.abs(result.totalSec) / 86400).toFixed(4)} tone="green" />
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

const Box = ({ label, value, tone }) => {
  const c = tone === 'saffron' ? 'text-saffron-700' : tone === 'green' ? 'text-indgreen-700' : 'text-bharat-navy';
  return (
    <div className="bg-white rounded-md p-2.5 border border-slate-100">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`display-font font-semibold mt-0.5 ${c}`}>{value}</div>
    </div>
  );
};

export default TimeCalculator;
